// Camera math for the demo figure: presets and drag-to-rotate. Pure functions, no three.js, so they are cheap to test.
// The figure faces +z, y is up. Azimuth 0 looks at the figure straight from the front; positive azimuth walks
// the camera around toward the figure's left side (+x).

export type PresetName = 'front' | 'side' | 'threeQuarter';

export interface View {
  /** Degrees around the figure. */
  azimuth: number;
  /** Degrees above the figure's middle. */
  elevation: number;
  /** Shown like a mirror (left and right swapped), as the user sees themselves in the camera picture. */
  mirrored: boolean;
}

export const PRESETS: Record<PresetName, View> = {
  // About 30 degrees off the front and mirrored: matches where the phone stands in a session.
  front: { azimuth: 30, elevation: 8, mirrored: true },
  // Straight from the side: back angle, hip hinge and knee position.
  side: { azimuth: 90, elevation: 4, mirrored: false },
  // From behind and to the side (the front preset already covers the front diagonal): back line and kettlebell path.
  threeQuarter: { azimuth: 140, elevation: 14, mirrored: false },
};

export const PRESET_LABELS: Record<PresetName, string> = { front: 'Front', side: 'Side', threeQuarter: '3/4 back' };

export const ELEVATION_LIMITS = [-10, 70] as const;

/** Camera position for a view around `target` at `distance`. */
export function orbitPosition(view: Pick<View, 'azimuth' | 'elevation'>, target: readonly number[], distance: number): [number, number, number] {
  const az = (view.azimuth * Math.PI) / 180;
  const el = (view.elevation * Math.PI) / 180;
  const flat = Math.cos(el) * distance;
  return [target[0]! + Math.sin(az) * flat, target[1]! + Math.sin(el) * distance, target[2]! + Math.cos(az) * flat];
}

/** Distance that fits a sphere of `radius` into a camera with this vertical field of view and aspect ratio. */
export function fitDistance(radius: number, fovDeg: number, aspect: number, margin = 1.08): number {
  const v = (fovDeg * Math.PI) / 360;
  const h = Math.atan(Math.tan(v) * aspect);
  return (radius * margin) / Math.sin(Math.min(v, h));
}

/**
 * Turns a view by a pointer drag of dx, dy pixels. A mirrored view swaps left and right on screen,
 * so the horizontal drag is reversed there to keep "drag right turns the figure right".
 */
export function dragView(view: View, dx: number, dy: number, degPerPixel = 0.4): View {
  const sign = view.mirrored ? 1 : -1;
  const azimuth = normalizeAngle(view.azimuth + sign * dx * degPerPixel);
  const elevation = Math.min(ELEVATION_LIMITS[1], Math.max(ELEVATION_LIMITS[0], view.elevation + dy * degPerPixel));
  return { ...view, azimuth, elevation };
}

export function normalizeAngle(deg: number): number {
  const a = ((deg % 360) + 540) % 360 - 180;
  return a === -180 ? 180 : a;
}
